import { createSelector } from '@reduxjs/toolkit';

import choiceSelector from '@/features/choice/selectors/choice';
import filterItemsSelector from '@/features/filters/selectors/items';
import { FilterItemType, FilterOptionType } from '@/features/filters/types';
import {
  IChoiceHistoryItem,
  IExtendedChoiceHistoryItem,
} from '@/interfaces/choice.interfaces';
import { FilterBlocksEnum } from '@/interfaces/constructor.interfaces';

const prepareHistoryItem = (
  historyItem: IChoiceHistoryItem,
  filterItems: FilterItemType[],
): IExtendedChoiceHistoryItem | null => {
  const filterItem = filterItems.find(item => item.key === historyItem.key);
  if (!filterItem) {
    return null;
  }
  switch (filterItem.type) {
    case FilterBlocksEnum.FilterLocation:
    case FilterBlocksEnum.FilterMultipleSelect:
    case FilterBlocksEnum.FilterCheckboxGroup:
    case FilterBlocksEnum.FilterModelCheckboxGroup:
    case FilterBlocksEnum.FilterColorPicker:
    case FilterBlocksEnum.FilterDuoColorPicker: {
      const options = (filterItem.props?.options || []) as FilterOptionType[];
      const historyOption = options.find(option => {
        return option?.value === historyItem.value;
      });
      if (!historyOption) {
        return null;
      }
      const values = (filterItem.value ?? []) as any[];
      const valueOnDeletion = values.filter(v => v !== historyItem.value);
      return {
        ...historyItem,
        valueOnDeletion: valueOnDeletion.length ? valueOnDeletion : undefined,
        label: historyOption.label,
      };
    }
    case FilterBlocksEnum.FilterSelect: {
      const options = (filterItem.props?.options || []) as FilterOptionType[];
      const historyOption = options.find(option => {
        return option?.value === historyItem.value;
      });
      if (!historyOption) {
        return null;
      }
      return {
        ...historyItem,
        valueOnDeletion: undefined,
        label: historyOption.label,
      };
    }
    case FilterBlocksEnum.FilterSlider: {
      return {
        ...historyItem,
        valueOnDeletion: undefined,
        label: historyItem.value,
      };
    }
    case FilterBlocksEnum.FilterRangeSlider: {
      if (!Array.isArray(historyItem.value) || historyItem.value.length < 2) {
        return null;
      }
      const defaultFormatter = (value: any) => value;
      const formatter = (filterItem.props?.valueFormatter ??
        defaultFormatter) as typeof defaultFormatter;
      return {
        ...historyItem,
        valueOnDeletion: undefined,
        label: `${formatter(historyItem.value[0])} — ${formatter(historyItem.value[1])}`,
      };
    }
    default:
      return null;
  }
};

/**
 * Получить расширенную историю на основе данных из фильтров
 */
const historyItemsSelector = createSelector(
  [choiceSelector, filterItemsSelector],
  (choice, filterItems) => {
    const extendedHistoryItems = choice.history.reduce<IExtendedChoiceHistoryItem[]>(
      (acc, historyItem) => {
        const extendedHistoryItem = prepareHistoryItem(historyItem, filterItems);
        if (!extendedHistoryItem) {
          return acc;
        }
        return [...acc, extendedHistoryItem];
      },
      [],
    );
    return extendedHistoryItems;
  },
);

export default historyItemsSelector;
